import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AddEvent.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube, faPaintBrush, faRuler, faImage, faArrowRight } from '@fortawesome/free-solid-svg-icons';

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

function AddEvent({ onAddEvent, setEventDetails }) {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [eventType, setEventType] = useState("");
  const [spansaour, setSpansaour] = useState("");
  const [venue, setVenue] = useState("");
  const [budget, setBudget] = useState("");
  const [highlights, setHighlights] = useState("");
  
  const [stallPrice, setStallPrice] = useState(1000);
  const [ticketPrice, setTicketPrice] = useState(100);
  const [standingCapacity, setStandingCapacity] = useState(0);
  const [stallPattern, setStallPattern] = useState("full");
  const [selectedLayoutType, setSelectedLayoutType] = useState("predefined");
  const [hasSeating, setHasSeating] = useState(true);
  const [seatingType, setSeatingType] = useState("full");
  const [seatRows, setSeatRows] = useState(10);
  const [seatColumns, setSeatColumns] = useState(10);
  const [totalSeats, setTotalSeats] = useState(100);
  const [showEntrance, setShowEntrance] = useState(true);
  const [showExit, setShowExit] = useState(true);
  const [showStage, setShowStage] = useState(true);
  const [showParking, setShowParking] = useState(true);
  const [parkingZones, setParkingZones] = useState(2);
  const [hasVIPParking, setHasVIPParking] = useState(false);
  const [showFoodCourt, setShowFoodCourt] = useState(false);
  const [showRestrooms, setShowRestrooms] = useState(false);
  const [showFirstAid, setShowFirstAid] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingEvents, setExistingEvents] = useState([]);

  useEffect(() => {
    fetchExistingEvents();
  }, []);

  const fetchExistingEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/events.php?all`);
      const data = await response.json();
      setExistingEvents(data);
    } catch (error) {
      console.error("Error fetching existing events:", error);
    }
  };

  const isDateAvailable = (selectedDate) => {
    if (!selectedDate) return true;
    const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
    const eventOnDate = existingEvents.find(event => event.eventDate === formattedDate);
    return !eventOnDate;
  };

  const getEventOnDate = (selectedDate) => {
    if (!selectedDate) return null;
    const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
    return existingEvents.find(event => event.eventDate === formattedDate);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    
    if (newDate) {
      const eventOnDate = getEventOnDate(newDate);
      if (eventOnDate) {
        alert(`⚠️ Event "${eventOnDate.eventName}" already exists on this date. Please choose another date.`);
      }
    }
  };

  const updateSeatCount = (rows, cols) => {
    const total = rows * cols;
    setTotalSeats(total);
    return total;
  };

  const handleRowsChange = (e) => {
    const rows = parseInt(e.target.value) || 1;
    setSeatRows(rows);
    updateSeatCount(rows, seatColumns);
  };

  const handleColumnsChange = (e) => {
    const cols = parseInt(e.target.value) || 1;
    setSeatColumns(cols);
    updateSeatCount(seatRows, cols);
  };

  const handleSeatingToggle = (e) => {
    const isEnabled = e.target.checked;
    setHasSeating(isEnabled);
    if (!isEnabled) {
      setTotalSeats(0);
    } else {
      setTotalSeats(seatRows * seatColumns);
    }
  };

  const handleCustomLayoutSelect = () => {
    if (!name || !date || !budget || !eventType) {
      alert("Please fill in all required basic details first (Name, Date, Budget, and Event Type)");
      setActiveTab('basic');
      return;
    }

    if (!isDateAvailable(date)) {
      const eventOnDate = getEventOnDate(date);
      alert(`❌ Cannot create event: "${eventOnDate.eventName}" already exists on this date. Please choose another date.`);
      return;
    }

    const formData = {
      name, date, eventType, spansaour, venue, budget, highlights,
      stallPrice, ticketPrice, standingCapacity,
      hasSeating, seatingType, seatRows, seatColumns, 
      totalSeats: hasSeating ? totalSeats : 0,
      showEntrance, showExit, showStage, showParking,
      parkingZones, hasVIPParking, showFoodCourt,
      showRestrooms, showFirstAid,
      isCustomLayout: true
    };
    
    sessionStorage.setItem('pendingEventData', JSON.stringify(formData));
    
    console.log("Navigating to layout builder with form data");
    
    navigate('/admin/layout-builder', { 
      state: { 
        fromAddEvent: true,
        eventName: name,
        eventType: eventType
      } 
    });
  };

  const handlePredefinedLayoutSelect = (pattern) => {
    if (!name || !date || !budget || !eventType) {
      alert("Please fill in all required basic details first (Name, Date, Budget, and Event Type)");
      setActiveTab('basic');
      return;
    }

    if (!isDateAvailable(date)) {
      const eventOnDate = getEventOnDate(date);
      alert(`❌ Cannot create event: "${eventOnDate.eventName}" already exists on this date. Please choose another date.`);
      return;
    }

    setSelectedLayoutType("predefined");
    setStallPattern(pattern);
    
    const confirmCreate = window.confirm(`Create event with ${pattern === 'full' ? 'Full Rectangle' : pattern === 'ushape' ? 'U-Shape' : 'Two Sides'} layout?`);
    
    if (confirmCreate) {
      const fakeEvent = { preventDefault: () => {} };
      handleSubmit(fakeEvent);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!isDateAvailable(date)) {
      const eventOnDate = getEventOnDate(date);
      alert(`❌ Cannot create event: "${eventOnDate.eventName}" already exists on this date. Please choose another date.`);
      return;
    }
    
    if (selectedLayoutType === "custom") {
      handleCustomLayoutSelect();
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };
      
      const eventData = {
        eventName: name.trim(),
        eventDate: formatDate(date),
        eventType: eventType.trim(),
        spansour: spansaour.trim() || "Unknown Sponsor",
        place: venue.trim() || "Unknown Venue",
        budget: budget ? Number(budget) : 0,
        highlights: highlights.trim() || "",
        stallPrice: stallPrice ? Number(stallPrice) : 8000,
        ticketPrice: ticketPrice ? Number(ticketPrice) : 1000,
        standingCapacity: standingCapacity ? Number(standingCapacity) : 0,
        stallPattern: stallPattern,
        hasSeating: hasSeating,
        seatingType: seatingType,
        seatRows: seatRows,
        seatColumns: seatColumns,
        totalSeats: hasSeating ? totalSeats : 0,
        showEntrance: showEntrance,
        showExit: showExit,
        showStage: showStage,
        showParking: showParking,
        parkingZones: parkingZones,
        hasVIPParking: hasVIPParking,
        showFoodCourt: showFoodCourt,
        showRestrooms: showRestrooms,
        showFirstAid: showFirstAid,
        isCustomLayout: false
      };
      
      console.log("Sending event data:", eventData);

      const res = await fetch(`${API_BASE_URL}/events.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventData),
      });

      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(responseData.error || "Failed to save event");
      }

      console.log("Event saved successfully:", responseData);
      
      await fetchExistingEvents();
      
      if (setEventDetails) {
        setEventDetails(prev => [...prev, responseData]);
      }
      
      if (onAddEvent) {
        onAddEvent(responseData);
      }
      
      setName("");
      setDate("");
      setEventType("");
      setSpansaour("");
      setVenue("");
      setBudget("");
      setHighlights("");
      setStallPrice(8000);
      setTicketPrice(1000);
      setStandingCapacity(0);
      setStallPattern("full");
      setSelectedLayoutType("predefined");
      setHasSeating(true);
      setSeatingType("full");
      setSeatRows(10);
      setSeatColumns(10);
      setTotalSeats(100);
      setActiveTab("basic");
      
      alert(`Event created successfully with ${stallPattern === 'full' ? 'Full Rectangle' : stallPattern === 'ushape' ? 'U-Shape' : 'Two Sides'} layout! 🎉`);
      
    } catch (error) {
      console.error("Error saving event:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToNextTab = (e) => {
    e.preventDefault();
    
    if (activeTab === 'basic') {
      if (!eventType) {
        alert("Please select an event type before proceeding.");
        return;
      }
      if (date && !isDateAvailable(date)) {
        const eventOnDate = getEventOnDate(date);
        alert(`⚠️ Event "${eventOnDate.eventName}" already exists on this date. Please choose another date before proceeding.`);
        return;
      }
      setActiveTab('stalls');
    }
    if (activeTab === 'stalls') setActiveTab('seating');
  };

  const goToPreviousTab = (e) => {
    e.preventDefault();
    if (activeTab === 'stalls') setActiveTab('basic');
    if (activeTab === 'seating') setActiveTab('stalls');
  };

  const handleTabClick = (tab, e) => {
    e.preventDefault();
    
    if (activeTab === 'basic' && tab !== 'basic') {
      if (!eventType) {
        alert("Please select an event type before proceeding.");
        return;
      }
      if (date && !isDateAvailable(date)) {
        const eventOnDate = getEventOnDate(date);
        alert(`⚠️ Event "${eventOnDate.eventName}" already exists on this date. Please choose another date before proceeding.`);
        return;
      }
    }
    
    setActiveTab(tab);
  };

  return (
    <div className="add-event-page">
      <div className="add-event-container">
        <div className="add-event-header">
          <h1>Create New Event</h1>
          <p>Fill in the details to set up your event</p>
        </div>

        <form onSubmit={handleSubmit} className="add-event-form">
          {/* Tab Navigation */}
          <div className="form-tabs">
            <button 
              type="button"
              className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`} 
              onClick={(e) => handleTabClick('basic', e)}
            >
              <span className="tab-icon">📋</span>
              <span>Basic Details</span>
            </button>
            <button 
              type="button"
              className={`tab-btn ${activeTab === 'stalls' ? 'active' : ''}`} 
              onClick={(e) => handleTabClick('stalls', e)}
            >
              <span className="tab-icon">🏪</span>
              <span>Stall Layout</span>
            </button>
            <button 
              type="button"
              className={`tab-btn ${activeTab === 'seating' ? 'active' : ''}`} 
              onClick={(e) => handleTabClick('seating', e)}
            >
              <span className="tab-icon">🪑</span>
              <span>Seating & Map</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content-wrapper">
            {/* Basic Details Tab */}
            {activeTab === 'basic' && (
              <div className="tab-pane fade-in">
                <div className="form-group">
                  <label className="form-label">
                    <span className="label-icon">🎉</span>
                    Event Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Royal Heritage Festia Grand Gala"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="label-icon">📅</span>
                      Event Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-input"
                      value={date}
                      onChange={handleDateChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                    {date && !isDateAvailable(date) && (
                      <small className="date-warning">
                        ⚠️ Event already exists on this date
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <span className="label-icon">🏷️</span>
                      Event Type <span className="required">*</span>
                    </label>
                    <select
                      className="form-input"
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      required
                    >
                      <option value="" disabled>Select event type</option>
                      <option value="Royal Heritage Festia">Royal Heritage Festia</option>
                      <option value="Royal Dinners">Royal Dinners</option>
                      <option value="Music & Concerts">Music & Concerts</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="label-icon">💰</span>
                      Budget <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-input"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      placeholder="Enter amount"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <span className="label-icon">🤝</span>
                      Sponsor
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={spansaour}
                      onChange={(e) => setSpansaour(e.target.value)}
                      placeholder="Sponsor name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="label-icon">📍</span>
                      Venue
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={venue}
                      onChange={(e) => setVenue(e.target.value)}
                      placeholder="Event location"
                    />
                  </div>
                </div>

                <div className="price-config-section">
                  <h3 className="section-subtitle">💰 Price Configuration</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-icon">🏪</span>
                        Stall Price (₹)
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        value={stallPrice}
                        onChange={(e) => setStallPrice(e.target.value)}
                        placeholder="Enter stall price"
                        min="0"
                        step="100"
                      />
                      <small className="price-hint">Default: ₹8000</small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-icon">🎫</span>
                        Ticket Price (₹)
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        value={ticketPrice}
                        onChange={(e) => setTicketPrice(e.target.value)}
                        placeholder="Enter ticket price"
                        min="0"
                        step="50"
                      />
                      <small className="price-hint">Default: ₹1000</small>
                    </div>
                  </div>
                </div>

                <div className="capacity-section">
                  <h3 className="section-subtitle">👥 Event Capacity</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-icon">🎟️</span>
                        Standing Event Capacity
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        value={standingCapacity}
                        onChange={(e) => setStandingCapacity(parseInt(e.target.value) || 0)}
                        placeholder="Enter maximum tickets (0 = unlimited)"
                        min="0"
                        step="1"
                      />
                      <small className="field-hint">
                        {hasSeating 
                          ? "Note: This only applies if you disable seating in the Seating tab" 
                          : "Maximum number of tickets that can be sold for this standing event. Enter 0 for unlimited."}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-group highlights-group">
                  <label className="form-label">
                    <span className="label-icon">✨</span>
                    Event Highlights
                  </label>
                  <textarea
                    className="form-textarea"
                    value={highlights}
                    onChange={(e) => setHighlights(e.target.value)}
                    placeholder="Describe the key highlights of your event..."
                    rows="5"
                  />
                  <small className="field-hint">
                    Add bullet points describing what makes your event special
                  </small>
                </div>
              </div>
            )}

            {/* Stall Layout Tab */}
            {activeTab === 'stalls' && (
              <div className="tab-pane fade-in">
                <h3 className="section-title">Choose Stall Layout Type</h3>
                
                <div className="layout-options-grid">
                  <div 
                    className={`layout-option-card ${selectedLayoutType === 'predefined' ? 'selected' : ''}`}
                    onClick={() => setSelectedLayoutType('predefined')}
                  >
                    <div className="layout-option-icon">📐</div>
                    <h4>Pre-defined Patterns</h4>
                    <p>Choose from our ready-made stall arrangements</p>
                    <ul className="layout-features">
                      <li>✓ Full Rectangle (50 stalls)</li>
                      <li>✓ U-Shape (40 stalls)</li>
                      <li>✓ Two Sides (30 stalls)</li>
                      <li>✓ Instant layout generation</li>
                    </ul>
                    {selectedLayoutType === 'predefined' && (
                      <div className="selected-badge">Selected</div>
                    )}
                  </div>

                  <div 
                    className={`layout-option-card custom ${selectedLayoutType === 'custom' ? 'selected' : ''}`}
                    onClick={handleCustomLayoutSelect}
                  >
                    <div className="layout-option-icon">🎨</div>
                    <h4>Custom Layout</h4>
                    <p>Design your own unique stall arrangement</p>
                    <ul className="layout-features">
                      <li>✓ Upload your venue blueprint</li>
                      <li>✓ Drag & drop stalls anywhere</li>
                      <li>✓ Add parking, stage, amenities</li>
                      <li>✓ Color-code by category</li>
                      <li>✓ Set individual stall prices</li>
                    </ul>
                    {selectedLayoutType === 'custom' && (
                      <div className="selected-badge">Selected</div>
                    )}
                    
                    {selectedLayoutType === 'custom' && (
                      <button 
                        type="button"
                        className="proceed-to-designer-btn"
                        onClick={handleCustomLayoutSelect}
                      >
                        Proceed to Layout Designer
                        <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
                      </button>
                    )}
                  </div>
                </div>

                {selectedLayoutType === 'predefined' && (
                  <div className="predefined-layouts-section">
                    <h3 className="section-title">Select Pre-defined Pattern</h3>
                    <div className="stall-options">
                      <div 
                        className={`stall-card ${stallPattern === 'full' ? 'selected' : ''}`}
                        onClick={() => handlePredefinedLayoutSelect('full')}
                      >
                        <div className="stall-preview full">
                          <div className="preview-grid">
                            <span></span><span></span><span></span><span></span>
                          </div>
                        </div>
                        <div className="stall-info">
                          <h4>Full Rectangle</h4>
                          <p className="stall-count">50 stalls</p>
                          <p className="stall-desc">Stalls on all 4 sides - Best for exhibitions</p>
                        </div>
                      </div>

                      <div 
                        className={`stall-card ${stallPattern === 'ushape' ? 'selected' : ''}`}
                        onClick={() => handlePredefinedLayoutSelect('ushape')}
                      >
                        <div className="stall-preview ushape">
                          <div className="preview-grid">
                            <span></span><span></span><span></span>
                          </div>
                        </div>
                        <div className="stall-info">
                          <h4>U-Shape</h4>
                          <p className="stall-count">40 stalls</p>
                          <p className="stall-desc">Stalls on top, left, and right - Best for concerts</p>
                        </div>
                      </div>

                      <div 
                        className={`stall-card ${stallPattern === 'twosides' ? 'selected' : ''}`}
                        onClick={() => handlePredefinedLayoutSelect('twosides')}
                      >
                        <div className="stall-preview twosides">
                          <div className="preview-grid">
                            <span></span><span></span>
                          </div>
                        </div>
                        <div className="stall-info">
                          <h4>Two Sides Only</h4>
                          <p className="stall-count">30 stalls</p>
                          <p className="stall-desc">Stalls on left and right - Best for weddings</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Seating & Map Tab */}
            {activeTab === 'seating' && (
              <div className="tab-pane fade-in">
                <h3 className="section-title">Seating Configuration</h3>
                
                <div className="toggle-section">
                  <label className="toggle-label">
                    <span className="toggle-text">Enable Seating</span>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={hasSeating}
                        onChange={handleSeatingToggle}  
                      />
                      <span className="toggle-slider"></span>
                    </div>
                  </label>
                  <p className="toggle-description">
                    {hasSeating 
                      ? '✅ Assigned seating with row and column layout' 
                      : '❌ General admission - Standing only'}
                  </p>
                </div>

                {hasSeating && (
                  <>
                    <div className="seating-type-section">
                      <h4 className="subsection-title">Seating Type</h4>
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="seatingType"
                            value="full"
                            checked={seatingType === 'full'}
                            onChange={(e) => setSeatingType(e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-text">Full Seating - All seats available</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="seatingType"
                            value="partial"
                            checked={seatingType === 'partial'}
                            onChange={(e) => setSeatingType(e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-text">Partial Seating - Some seats available</span>
                        </label>
                      </div>
                    </div>

                    <div className="dimensions-section">
                      <h4 className="subsection-title">Seating Grid Dimensions</h4>
                      <div className="dimensions-grid">
                        <div className="dimension-input">
                          <label>Rows (1-50)</label>
                          <input
                            type="number"
                            value={seatRows}
                            onChange={handleRowsChange}
                            min="1"
                            max="50"
                          />
                        </div>
                        <div className="dimension-input">
                          <label>Columns (1-50)</label>
                          <input
                            type="number"
                            value={seatColumns}
                            onChange={handleColumnsChange}
                            min="1"
                            max="50"
                          />
                        </div>
                      </div>
                      <div className="total-seats">
                        <span>Total Seats:</span>
                        <strong>{totalSeats}</strong>
                        <small>({seatRows} rows × {seatColumns} columns)</small>
                      </div>
                    </div>
                  </>
                )}

                <h3 className="section-title mt-4">Map Elements</h3>
                
                <div className="map-elements-grid">
                  <div className="map-element-group">
                    <h4 className="subsection-title">Essential Elements</h4>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showEntrance}
                          onChange={(e) => setShowEntrance(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🚪 Entrance (North)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showExit}
                          onChange={(e) => setShowExit(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🚪 Exit (South)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showStage}
                          onChange={(e) => setShowStage(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🎪 Stage</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showParking}
                          onChange={(e) => setShowParking(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🅿️ Parking</span>
                      </label>
                    </div>
                  </div>

                  <div className="map-element-group">
                    <h4 className="subsection-title">Amenities</h4>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showFoodCourt}
                          onChange={(e) => setShowFoodCourt(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🍔 Food Court</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showRestrooms}
                          onChange={(e) => setShowRestrooms(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🚻 Restrooms</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={showFirstAid}
                          onChange={(e) => setShowFirstAid(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">🏥 First Aid</span>
                      </label>
                    </div>
                  </div>
                </div>

                {showParking && (
                  <div className="parking-config">
                    <h4 className="subsection-title">Parking Configuration</h4>
                    <div className="parking-controls">
                      <div className="parking-zones">
                        <label>Number of Zones:</label>
                        <select 
                          value={parkingZones}
                          onChange={(e) => setParkingZones(parseInt(e.target.value))}
                        >
                          <option value="1">1 Zone</option>
                          <option value="2">2 Zones</option>
                          <option value="3">3 Zones</option>
                        </select>
                      </div>
                      <label className="checkbox-label vip-parking">
                        <input
                          type="checkbox"
                          checked={hasVIPParking}
                          onChange={(e) => setHasVIPParking(e.target.checked)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">✨ VIP Parking Available</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-navigation">
            {activeTab !== 'basic' && (
              <button 
                type="button"
                className="nav-btn prev"
                onClick={goToPreviousTab}
              >
                ← Previous
              </button>
            )}
            
            {activeTab !== 'seating' ? (
              <button 
                type="button"
                className="nav-btn next"
                onClick={goToNextTab}
              >
                Next →
              </button>
            ) : (
              <button 
                type="submit"
                className="nav-btn submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Event 🎉'}
              </button>
            )}
          </div>
        </form>
      </div>

      <style jsx>{`
        .add-event-page {
          min-height: 100vh;
          background: #f5f7fa;
          padding: 40px 20px;
        }

        .add-event-container {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .add-event-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 30px;
          text-align: center;
        }

        .add-event-header h1 {
          margin: 0 0 8px 0;
          font-size: 28px;
        }

        .add-event-header p {
          margin: 0;
          opacity: 0.9;
        }

        .add-event-form {
          padding: 30px;
        }

        .form-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 10px;
        }

        .tab-btn {
          flex: 1;
          padding: 12px 20px;
          background: transparent;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #64748b;
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .tab-content-wrapper {
          min-height: 500px;
        }

        .tab-pane {
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          margin-bottom: 8px;
          color: #334155;
        }

        .required {
          color: #ef4444;
        }

        .form-input, .form-select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s;
        }

        .form-input:focus, .form-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
        }

        .form-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          resize: vertical;
          font-family: inherit;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #667eea;
        }

        .section-subtitle {
          font-size: 18px;
          font-weight: 600;
          margin: 20px 0 15px;
          color: #1e293b;
        }

        .price-hint, .field-hint {
          display: block;
          font-size: 12px;
          color: #94a3b8;
          margin-top: 5px;
        }

        .date-warning {
          display: block;
          color: #ef4444;
          font-size: 12px;
          margin-top: 5px;
        }

        .layout-options-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 30px;
        }

        .layout-option-card {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          cursor: pointer;
          transition: all 0.3s;
          text-align: center;
          position: relative;
        }

        .layout-option-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        }

        .layout-option-card.selected {
          border-color: #667eea;
          background: #f5f3ff;
        }

        .layout-option-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .layout-option-card h4 {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 8px 0;
          color: #1e293b;
        }

        .layout-option-card p {
          font-size: 14px;
          color: #64748b;
          margin-bottom: 16px;
        }

        .layout-features {
          text-align: left;
          font-size: 13px;
          color: #475569;
          margin: 0;
          padding-left: 20px;
        }

        .layout-features li {
          margin-bottom: 6px;
        }

        .selected-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: #667eea;
          color: white;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .proceed-to-designer-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          margin-top: 16px;
          transition: all 0.3s;
          width: 100%;
        }

        .proceed-to-designer-btn:hover {
          background: #5a67d8;
          transform: translateY(-2px);
        }

        .predefined-layouts-section {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .stall-options {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-top: 20px;
        }

        .stall-card {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.3s;
          text-align: center;
        }

        .stall-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        }

        .stall-card.selected {
          border-color: #667eea;
          background: #f5f3ff;
        }

        .stall-preview {
          width: 100%;
          height: 80px;
          background: #e2e8f0;
          border-radius: 8px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .preview-grid {
          display: grid;
          gap: 4px;
          width: 80%;
        }

        .preview-grid span {
          height: 8px;
          background: #94a3b8;
          border-radius: 4px;
        }

        .stall-info h4 {
          margin: 0 0 4px 0;
          font-size: 16px;
        }

        .stall-count {
          font-size: 12px;
          color: #667eea;
          font-weight: 600;
          margin: 0 0 4px 0;
        }

        .stall-desc {
          font-size: 11px;
          color: #64748b;
          margin: 0;
        }

        .form-navigation {
          display: flex;
          justify-content: space-between;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .nav-btn {
          padding: 12px 30px;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
          border: none;
        }

        .nav-btn.prev {
          background: #f1f5f9;
          color: #475569;
        }

        .nav-btn.prev:hover {
          background: #e2e8f0;
        }

        .nav-btn.next {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .nav-btn.next:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102,126,234,0.4);
        }

        .nav-btn.submit {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .nav-btn.submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(16,185,129,0.4);
        }

        .toggle-section {
          background: #f8fafc;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .toggle-label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
        }

        .toggle-text {
          font-weight: 500;
          color: #1e293b;
        }

        .toggle-switch {
          position: relative;
          width: 50px;
          height: 24px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          transition: 0.3s;
          border-radius: 24px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #667eea;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(26px);
        }

        .radio-group {
          display: flex;
          gap: 20px;
          margin-top: 10px;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .dimensions-section {
          background: #f8fafc;
          padding: 20px;
          border-radius: 12px;
          margin: 20px 0;
        }

        .dimensions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin: 15px 0;
        }

        .dimension-input label {
          display: block;
          font-size: 12px;
          margin-bottom: 5px;
          color: #64748b;
        }

        .dimension-input input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }

        .total-seats {
          text-align: center;
          padding: 10px;
          background: #e2e8f0;
          border-radius: 8px;
          margin-top: 15px;
        }

        .total-seats strong {
          font-size: 24px;
          color: #667eea;
          margin: 0 10px;
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
        }

        .map-elements-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin: 20px 0;
        }

        .map-element-group {
          background: #f8fafc;
          padding: 20px;
          border-radius: 12px;
        }

        .parking-config {
          background: #f8fafc;
          padding: 20px;
          border-radius: 12px;
          margin-top: 20px;
        }

        .parking-controls {
          display: flex;
          gap: 20px;
          align-items: center;
          flex-wrap: wrap;
        }

        .parking-zones select {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-left: 10px;
        }

        @media (max-width: 768px) {
          .form-row, .layout-options-grid, .stall-options, .dimensions-grid, .map-elements-grid {
            grid-template-columns: 1fr;
          }
          
          .form-tabs {
            flex-direction: column;
          }
          
          .add-event-container {
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default AddEvent;